import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { usePatients } from '../hooks/usePatients';
import { useEngagedPatients } from '../hooks/useEngagedPatients';
import { useClinicOnboarding } from '../hooks/useClinicOnboarding';
import { useProviderFilter } from '../hooks/useProviderFilter';
import { useTriageGrouping } from '../hooks/useTriageGrouping';
import PatientDrawer from '../components/patients/PatientDrawer';
import UploadWizard from '../components/patients/UploadWizard';
import GatedOnboarding from '../components/patients/GatedOnboarding';
import SendMessageDrawer from '../components/patients/SendMessageDrawer';
import RiskGroup from '../components/patients/RiskGroup';
import ProviderFilterDropdown from '../components/patients/ProviderFilterDropdown';
import FilterBanner from '../components/patients/FilterBanner';
import SelectionBar from '../components/patients/SelectionBar';
import StatCard from '../components/ui/StatCard';
import { Input } from '../components/shadcn/input';
import { Button } from '../components/shadcn/button';
import {
  MessageSquare,
  Upload,
  Search,
  AlertTriangle,
  Clock,
  CheckCircle2,
  TrendingUp,
  RotateCcw,
  UserCheck,
  Eye,
} from 'lucide-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const PatientsTriage = () => {
  const { patients, loading, updatePatient, refetch } = usePatients();
  const { engagedNotBookedPatients, loading: engagedLoading, stats: engagedStats } = useEngagedPatients();
  const { hasCompletedOnboarding, loading: onboardingLoading, markOnboardingComplete } = useClinicOnboarding();

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPatientIds, setSelectedPatientIds] = useState([]);
  const [activeFilter, setActiveFilter] = useState(null);

  // Section open/close state
  const [openSections, setOpenSections] = useState({
    engaged: true, followUp: true, urgent: true, medium: true, low: false, responded: false,
  });
  const [showAllSections, setShowAllSections] = useState({
    engaged: false, followUp: false, urgent: false, medium: false, low: false, responded: false,
  });

  // Section refs for scrolling
  const sectionRefs = {
    engaged: useRef(null), followUp: useRef(null), urgent: useRef(null),
    medium: useRef(null), low: useRef(null), responded: useRef(null),
  };

  // Drawer state
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isMessageDrawerOpen, setIsMessageDrawerOpen] = useState(false);
  const [isUploadWizardOpen, setIsUploadWizardOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);

  // Extracted hooks
  const providerFilter = useProviderFilter(patients);
  const { followUpPatients, urgentPatients, mediumPatients, lowPatients, respondedPatients, stats } =
    useTriageGrouping(patients, searchQuery, activeFilter, providerFilter.selectedProviders);

  // Toggle helpers
  const toggleSection = (section) => setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  const toggleShowAll = (section) => setShowAllSections(prev => ({ ...prev, [section]: !prev[section] }));
  const toggleFilter = (filterName) => setActiveFilter(prev => prev === filterName ? null : filterName);

  // Handlers
  const handlePatientClick = (patient) => {
    setSelectedPatient(patient);
    setIsDrawerOpen(true);
  };

  const handleSendMessage = (patient, e) => {
    e?.stopPropagation();
    setSelectedPatient(patient);
    setIsMessageDrawerOpen(true);
  };

  const toggleSelect = (patientId) => {
    setSelectedPatientIds(prev =>
      prev.includes(patientId)
        ? prev.filter(id => id !== patientId)
        : [...prev, patientId]
    );
  };

  const handlePatientUpdate = async (id, updates) => {
    setSelectedPatient(prev => prev?.id === id ? { ...prev, ...updates } : prev);
    await updatePatient(id, updates);
  };

  const handleUploadComplete = () => {
    setIsUploadWizardOpen(false);
    markOnboardingComplete();
    providerFilter.setSelectedProviders([]);
    refetch();
  };

  const formatLastTouch = (date) => {
    if (!date) return 'Never';
    const d = dayjs(date);
    if (!d.isValid()) return 'Never';
    if (d.isSame(dayjs(), 'day')) return 'Today';
    if (d.isSame(dayjs().subtract(1, 'day'), 'day')) return 'Yesterday';
    return d.fromNow();
  };

  // Early returns
  if (onboardingLoading) return null;

  if (hasCompletedOnboarding === false) {
    return (
      <GatedOnboarding
        onComplete={() => {
          markOnboardingComplete();
          refetch();
        }}
      />
    );
  }

  // Engaged patients filtered for display
  const filteredEngaged = engagedNotBookedPatients
    .filter(p => p.needsNudge)
    .filter(p => {
      if (!searchQuery.trim()) return true;
      const query = searchQuery.toLowerCase();
      return p.name?.toLowerCase().includes(query) || p.email?.toLowerCase().includes(query) || p.phone?.includes(query);
    })
    .filter(p => activeFilter === 'engaged' || !activeFilter || activeFilter === 'toContact');

  const totalFilteredCount = followUpPatients.length + urgentPatients.length + mediumPatients.length + lowPatients.length + respondedPatients.length;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        {/* Sub-navigation tabs */}
        <div className="flex items-center gap-1 border-b border-slate-200">
          <Link to="/patients" className="px-4 py-2 text-sm font-medium text-slate-900 border-b-2 border-slate-900">
            Triage
          </Link>
          <Link
            to="/patients/engaged"
            className="px-4 py-2 text-sm font-medium text-slate-500 hover:text-slate-700 border-b-2 border-transparent hover:border-slate-300 transition-colors flex items-center gap-2"
          >
            <Eye className="h-4 w-4" />
            Engaged - Not Booked
            {engagedStats.needsNudge > 0 && (
              <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-purple-100 text-purple-700 rounded-full">
                {engagedStats.needsNudge}
              </span>
            )}
          </Link>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-4">
          <StatCard
            value={stats.remaining}
            label="To Contact"
            icon={TrendingUp}
            accent={activeFilter === 'toContact' ? 'success' : 'default'}
            onClick={() => toggleFilter('toContact')}
            isActive={activeFilter === 'toContact'}
          />
          <StatCard
            value={stats.contacted + stats.followUp}
            label="In Progress"
            icon={MessageSquare}
            accent={activeFilter === 'contacted' ? 'success' : 'default'}
            onClick={() => toggleFilter('contacted')}
            isActive={activeFilter === 'contacted'}
          />
          <StatCard
            value={stats.resolved}
            label="Recovered"
            icon={CheckCircle2}
            accent={activeFilter === 'responded' ? 'success' : 'success'}
            onClick={() => toggleFilter('responded')}
            isActive={activeFilter === 'responded'}
          />
        </div>

        {/* Search and Provider Filter */}
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" strokeWidth={1.5} />
            <Input
              placeholder="Search by name, email, or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-10 bg-white border-slate-200 text-sm placeholder:text-slate-400 focus:ring-1 focus:ring-slate-300 focus:border-slate-300"
            />
          </div>
          <ProviderFilterDropdown {...providerFilter} />
        </div>

        <FilterBanner
          activeFilter={activeFilter}
          totalCount={totalFilteredCount}
          onClear={() => setActiveFilter(null)}
        />

        <SelectionBar
          count={selectedPatientIds.length}
          onClear={() => setSelectedPatientIds([])}
        />

        {/* Main Content */}
        {loading || engagedLoading ? (
          <div className="flex flex-col items-center justify-center py-20 text-slate-400">
            <div className="w-6 h-6 border-2 border-slate-200 border-t-slate-500 rounded-full animate-spin mb-3" />
            <p className="text-xs">Loading...</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Engaged - Not Booked */}
            {filteredEngaged.length > 0 && (
              <RiskGroup
                title="Engaged - Not Booked"
                subtitle="Read message 48+ hours ago, needs follow-up"
                icon={Eye}
                patients={filteredEngaged}
                isOpen={openSections.engaged}
                onToggle={() => toggleSection('engaged')}
                accentColor="purple"
                onPatientClick={handlePatientClick}
                onSendMessage={handleSendMessage}
                selectedIds={selectedPatientIds}
                onToggleSelect={toggleSelect}
                formatLastTouch={(date, patient) => {
                  if (patient?.engagedAt) {
                    const d = dayjs(patient.engagedAt);
                    if (d.isValid()) return `Engaged ${d.fromNow()}`;
                  }
                  return formatLastTouch(date);
                }}
                showAll={showAllSections.engaged}
                onShowAll={() => toggleShowAll('engaged')}
                sectionRef={sectionRefs.engaged}
                headerLink="/patients/engaged"
              />
            )}

            <RiskGroup
              title="Follow Up Needed"
              subtitle="Contacted 3+ days ago, no response yet"
              icon={RotateCcw}
              patients={followUpPatients}
              isOpen={openSections.followUp}
              onToggle={() => toggleSection('followUp')}
              accentColor="orange"
              onPatientClick={handlePatientClick}
              onSendMessage={handleSendMessage}
              selectedIds={selectedPatientIds}
              onToggleSelect={toggleSelect}
              formatLastTouch={formatLastTouch}
              showAll={showAllSections.followUp}
              onShowAll={() => toggleShowAll('followUp')}
              sectionRef={sectionRefs.followUp}
            />

            <RiskGroup
              title="Urgent"
              subtitle="High-risk patients needing first contact"
              icon={AlertTriangle}
              patients={urgentPatients}
              isOpen={openSections.urgent}
              onToggle={() => toggleSection('urgent')}
              accentColor="red"
              onPatientClick={handlePatientClick}
              onSendMessage={handleSendMessage}
              selectedIds={selectedPatientIds}
              onToggleSelect={toggleSelect}
              formatLastTouch={formatLastTouch}
              showAll={showAllSections.urgent}
              onShowAll={() => toggleShowAll('urgent')}
              sectionRef={sectionRefs.urgent}
            />

            <RiskGroup
              title="Needs Attention"
              subtitle="Follow up when urgent is complete"
              icon={Clock}
              patients={mediumPatients}
              isOpen={openSections.medium}
              onToggle={() => toggleSection('medium')}
              accentColor="amber"
              onPatientClick={handlePatientClick}
              onSendMessage={handleSendMessage}
              selectedIds={selectedPatientIds}
              onToggleSelect={toggleSelect}
              formatLastTouch={formatLastTouch}
              showAll={showAllSections.medium}
              onShowAll={() => toggleShowAll('medium')}
              sectionRef={sectionRefs.medium}
            />

            <RiskGroup
              title="Routine"
              subtitle="Standard check-ins"
              icon={CheckCircle2}
              patients={lowPatients}
              isOpen={openSections.low}
              onToggle={() => toggleSection('low')}
              accentColor="emerald"
              onPatientClick={handlePatientClick}
              onSendMessage={handleSendMessage}
              selectedIds={selectedPatientIds}
              onToggleSelect={toggleSelect}
              formatLastTouch={formatLastTouch}
              showAll={showAllSections.low}
              onShowAll={() => toggleShowAll('low')}
              sectionRef={sectionRefs.low}
            />

            <RiskGroup
              title="Resolved"
              subtitle="Recovered or closed cases"
              icon={UserCheck}
              patients={respondedPatients}
              isOpen={openSections.responded}
              onToggle={() => toggleSection('responded')}
              accentColor="blue"
              onPatientClick={handlePatientClick}
              onSendMessage={handleSendMessage}
              selectedIds={selectedPatientIds}
              onToggleSelect={toggleSelect}
              formatLastTouch={formatLastTouch}
              hideProgress
              showAll={showAllSections.responded}
              onShowAll={() => toggleShowAll('responded')}
              sectionRef={sectionRefs.responded}
            />

            {/* Empty State - All caught up */}
            {followUpPatients.length === 0 && urgentPatients.length === 0 && mediumPatients.length === 0 && lowPatients.length === 0 && respondedPatients.length > 0 && (
              <div className="flex flex-col items-center justify-center py-16 bg-white rounded-lg border border-slate-200">
                <CheckCircle2 className="h-8 w-8 text-emerald-500 mb-3" strokeWidth={1.5} />
                <h3 className="text-sm font-medium text-slate-900">All caught up</h3>
                <p className="text-xs text-slate-500 mt-1">No patients need outreach right now</p>
              </div>
            )}

            {/* Empty State - No patients */}
            {patients.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 bg-white rounded-lg border border-slate-200">
                <Upload className="h-8 w-8 text-slate-300 mb-3" strokeWidth={1.5} />
                <h3 className="text-sm font-medium text-slate-900">No patients yet</h3>
                <p className="text-xs text-slate-500 mt-1 mb-4">Upload a patient list to get started</p>
                <Button onClick={() => setIsUploadWizardOpen(true)} variant="outline" className="text-sm">
                  <Upload className="h-4 w-4 mr-2" strokeWidth={1.5} />
                  Upload List
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Drawers and Modals */}
        <PatientDrawer
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          patient={selectedPatient}
          onPatientUpdate={handlePatientUpdate}
        />
        <SendMessageDrawer
          isOpen={isMessageDrawerOpen}
          onClose={() => setIsMessageDrawerOpen(false)}
          patient={selectedPatient}
        />
        <UploadWizard
          isOpen={isUploadWizardOpen}
          onClose={() => setIsUploadWizardOpen(false)}
          onComplete={handleUploadComplete}
        />
      </div>
    </div>
  );
};

export default PatientsTriage;
